import {RootState} from 'store'
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {
  FiveIndicatorResponse,
  IndividualSummarySearchRequest,
  searchIndividualSummaryFiveIndicatorsApi,
} from 'api/analysisApi'
import {openFailureModalAction} from 'features/modal/alertSlice'
import {getDefaultPeriodString, periodStringToDate} from 'helpers/dateHelper'

export const searchEegSummaryFiveIndicators = createAsyncThunk(
  '/api/v1/analyze-follow-up/five-indicator',
  async (
    payload: IndividualSummarySearchRequest,
    {rejectWithValue, dispatch},
  ) => {
    try {
      const response = await searchIndividualSummaryFiveIndicatorsApi(payload)
      return response.page
    } catch (err) {
      await dispatch(openFailureModalAction(err.message))
      return rejectWithValue(err.message)
    }
  },
)

interface EegSummaryState {
  loading: boolean
  error: any
  items: FiveIndicatorResponse[]
  period: PeriodString
  search: AnalysisSearchKind
  paging: Paging
  pagingInfo: {
    totalPages: number
    totalElements: number
  }
}

const initialState: EegSummaryState = {
  loading: false,
  error: null,
  items: [],
  period: getDefaultPeriodString(),
  search: {searchName: 'NAME', searchValue: ''},
  paging: {page: 0, size: 10},
  pagingInfo: {
    totalPages: 1,
    totalElements: 0,
  },
}

const slice = createSlice({
  name: 'eegSummaryFiveIndicatorsSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(searchEegSummaryFiveIndicators.pending, (state, action) => {
      state.loading = true
      state.error = null

      const {period, search, paging} = action.meta.arg
      state.period = period
      state.search = search
      state.paging = paging
    })
    builder.addCase(
      searchEegSummaryFiveIndicators.fulfilled,
      (state, action) => {
        state.loading = false

        const {
          content: items,
          number: page,
          size,
          totalPages,
          totalElements,
        } = action.payload
        state.items = items
        state.loading = false
        state.error = null
        state.paging.page = page
        state.paging.size = size
        state.pagingInfo.totalPages = totalPages
        state.pagingInfo.totalElements = totalElements
      },
    )
    builder.addCase(
      searchEegSummaryFiveIndicators.rejected,
      (state, action) => {
        state.loading = false
        state.error = action.payload
      },
    )
  },
})

export default slice.reducer

export const selectLoadingFiveIndicators = (state: RootState) =>
  state.individualSummaryFiveIndicators.loading
export const selectErrorFiveIndicators = (state: RootState) =>
  state.individualSummaryFiveIndicators.error
export const selectItemsFiveIndicators = (state: RootState) =>
  state.individualSummaryFiveIndicators.items
export const selectPeriodFiveIndicators = (state: RootState) =>
  periodStringToDate(state.individualSummaryFiveIndicators.period)
export const selectSearchFiveIndicators = (state: RootState) =>
  state.individualSummaryFiveIndicators.search
export const selectPagingFiveIndicators = (state: RootState) =>
  state.individualSummaryFiveIndicators.paging
export const selectPagingInfoFiveIndicators = (state: RootState) =>
  state.individualSummaryFiveIndicators.pagingInfo

export const selectQueryFiveIndicators = (state: RootState) => ({
  period: state.individualSummaryFiveIndicators.period,
  search: state.individualSummaryFiveIndicators.search,
  paging: state.individualSummaryFiveIndicators.paging,
})
